// =============================================================================
// TOKENS DE NAVEGACIÓN - SISTEMA DE DISEÑO TADMIN
// =============================================================================
// Tokens para componentes de navegación: Sidebar, Breadcrumb

import colors from './colors.js';
import typography from './typography.js';
import spacing from './spacing.js';
import shadows from './shadows.js';
import borders from './borders.js';

// Desestructuración segura de typography
const typographyData = typography || {};

const fontSize = typographyData.fontSizes || {};
const fontWeight = typographyData.fontWeights || {};
const lineHeight = typographyData.lineHeights || {};

// =============================================================================
// TOKENS PARA SIDEBAR
// =============================================================================
export const sidebarTokens = {
  provider: {
    display: 'flex',
    minHeight: '100vh',
    width: '100%',
  },
  sidebar: {
    backgroundColor: colors.light.surface.default,
    color: colors.light.text.primary,
    display: 'flex',
    height: '100%',
    width: '16rem',
    flexDirection: 'column',
    transition: 'width 0.2s ease-linear',
  },
  sidebarCollapsed: {
    width: '3rem',
  },
  sidebarMobile: {
    backgroundColor: colors.light.surface.default,
    width: '16rem',
    padding: 0,
  },
  trigger: {
    height: '28px',
    width: '28px',
  },
  inset: {
    backgroundColor: colors.light.background.default,
    position: 'relative',
    display: 'flex',
    width: '100%',
    flex: 1,
    flexDirection: 'column',
  },
  input: {
    backgroundColor: colors.light.background.default,
    height: '32px',
    width: '100%',
    boxShadow: 'none',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.spacing[2],
    padding: spacing.spacing[2],
  },
  footer: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.spacing[2],
    padding: spacing.spacing[2],
  },
  separator: {
    backgroundColor: colors.light.border.default,
    margin: `0 ${spacing.spacing[2]}`,
    width: 'auto',
  },
  content: {
    display: 'flex',
    minHeight: 0,
    flex: 1,
    flexDirection: 'column',
    gap: spacing.spacing[2],
    overflow: 'auto',
  },
  group: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.spacing[1],
    padding: spacing.spacing[2],
  },
  groupLabel: {
    fontSize: fontSize.xs,
    fontWeight: fontWeight.medium,
    color: colors.light.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    padding: `0 ${spacing.spacing[2]}`,
    marginBottom: spacing.spacing[1],
  },
  groupContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.spacing[1],
  },
  menu: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.spacing[1],
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.spacing[2],
    padding: `${spacing.spacing[2]} ${spacing.spacing[3]}`,
    borderRadius: borders.radius.sm,
    fontSize: fontSize.sm,
    color: colors.light.text.primary,
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: colors.light.primary.light,
      color: colors.light.primary.main,
    },
    '&.active': {
      backgroundColor: colors.light.primary.main,
      color: colors.light.primary.contrast,
    },
  },
  menuButton: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.spacing[2],
    padding: `${spacing.spacing[2]} ${spacing.spacing[3]}`,
    borderRadius: borders.radius.sm,
    fontSize: fontSize.sm,
    color: colors.light.text.primary,
    cursor: 'pointer',
    border: 'none',
    backgroundColor: 'transparent',
    width: '100%',
    textAlign: 'left',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: colors.light.primary.light,
      color: colors.light.primary.main,
    },
  },
  menuSubItem: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.spacing[2],
    padding: `${spacing.spacing[1]} ${spacing.spacing[3]} ${spacing.spacing[1]} ${spacing.spacing[6]}`,
    borderRadius: borders.radius.sm,
    fontSize: fontSize.sm,
    color: colors.light.text.secondary,
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: colors.light.primary.light,
      color: colors.light.primary.main,
    },
  },
};

// =============================================================================
// TOKENS PARA TABS
// =============================================================================
export const tabsTokens = {
  list: {
    display: 'flex',
    height: '40px',
    alignItems: 'center',
    justifyContent: 'flex-start',
    borderRadius: borders.radius.lg,
    backgroundColor: colors.light.surface.subtle,
    padding: spacing.spacing[1],
    border: `1px solid ${colors.light.border.default}`,
    gap: spacing.spacing[1],
  },
  trigger: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    borderRadius: borders.radius.md,
    paddingY: spacing.spacing[2],
    paddingX: spacing.spacing[3],
    fontSize: fontSize.sm,
    fontWeight: fontWeight.medium,
    transition: 'all 0.2s ease-in-out',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    color: colors.light.text.secondary,
    border: 'none',
    outline: 'none',
    states: {
      inactive: {
        backgroundColor: 'transparent',
        color: colors.light.text.secondary,
      },
      hover: {
        backgroundColor: colors.light.surface.default,
        color: colors.light.text.primary,
      },
      active: {
        backgroundColor: colors.light.background.default,
        color: colors.light.text.primary,
        boxShadow: shadows.base.sm,
        fontWeight: fontWeight.semiBold,
      },
      focus: {
        outline: `2px solid ${colors.light.primary.main}`,
        outlineOffset: '2px',
      },
      disabled: {
        pointerEvents: 'none',
        opacity: 0.5,
      },
    },
  },
  content: {
    marginTop: spacing.spacing[4],
    outline: 'none',
    focus: {
      outline: `2px solid ${colors.light.primary.main}`,
      outlineOffset: '2px',
      borderRadius: borders.radius.sm,
    },
  },
};

// =============================================================================
// TOKENS PARA BREADCRUMB NAVIGATION
// =============================================================================
export const breadcrumbTokens = {
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.spacing[2],
    fontSize: fontSize.sm,
    color: colors.light.text.secondary,
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    color: colors.light.text.secondary,
    cursor: 'pointer',
    transition: 'color 0.2s ease-in-out',
    '&:hover': {
      color: colors.light.text.primary,
    },
  },
  activeItem: {
    color: colors.light.text.primary,
    fontWeight: fontWeight.medium,
  },
  separator: {
    color: colors.light.text.secondary,
    width: '16px',
    height: '16px',
  },
  homeButton: {
    display: 'flex',
    alignItems: 'center',
    color: colors.light.text.secondary,
    cursor: 'pointer',
    transition: 'color 0.2s ease-in-out',
    '&:hover': {
      color: colors.light.text.primary,
    },
  },
};